import React, { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';

import { AuthContext } from '../components/AuthProvider';

import { get_scans } from "../tools-network";
import { showTemporaryMessage } from "./Message";
import ScanLocationsMap from "./ScanLocationsMap";
import BearerMedicalDisplay from '../../../../shared/components/BearerMedicalDisplay';

/**
 * Format an ISO timestamp to a readable local string.
 * "2026-04-06T22:47:59.082Z" → "2026-04-06 22:47 CET"
 */
const formatTimestamp = (iso) => {
	if (!iso) return 'N/A';
	try {
		const d = new Date(iso);
		if (isNaN(d.getTime())) return iso;
		// Format: YYYY-MM-DD HH:MM <timezone>
		const pad = (n) => String(n).padStart(2, '0');
		const year = d.getFullYear();
		const month = pad(d.getMonth() + 1);
		const day = pad(d.getDate());
		const hours = pad(d.getHours());
		const minutes = pad(d.getMinutes());
		// Get short timezone name (e.g. CET, CEST)
		const tz = d.toLocaleTimeString('sv-SE', { timeZoneName: 'short' }).split(' ').pop();
		return `${year}-${month}-${day} ${hours}:${minutes} ${tz}`;
	} catch {
		return iso;
	}
};

const Scans = ({loginToken, scanToken}) => {
	const { t } = useTranslation();
	const { userType } = React.useContext(AuthContext);

	const [scans, setScans]					= useState([]);
	const [selectedScanInList, setSelectedScanInList]	= useState(null);
	const [showDetails, setShowDetails]		= useState(false);

	const [mapDisabledMessage, setMapDisabledMessage] = useState("");

	const locationX = selectedScanInList?.location ? selectedScanInList.location.x : null;
	const locationY = selectedScanInList?.location ? selectedScanInList.location.y : null;

	const [locations, setLocations] = useState([]);

	const updateListOfScans = async () => {
		if(scanToken == null) {
			setScans([]);
			return;
		}
		try {
			const res = await get_scans(loginToken, scanToken);
			if(res.status) {
				const scans = res.msg ? res.msg : [];
				setScans(scans);
			} else {
				showTemporaryMessage("Fetch scans FAILED!", res.msg, 'danger');
			}
		} catch (err) {
			showTemporaryMessage("Fetch scans FAILED!", err, 'danger');
		}
	};

	useEffect(() => {
		const convertedLocations = scans
			.filter(scan => scan.location && scan.location.x !== undefined && scan.location.y !== undefined)
			.map(scan => ({
				id: scan.id,
				x: scan?.location?.x,
				y: scan?.location?.y
			}));
		setLocations(convertedLocations);
	}, [scans]);

	useEffect(() => {
		// Potential reasons the map is not active
		if(scanToken == null) {
			setMapDisabledMessage(t("no_token_has_been_selected"));
		} else if(scans.length === 0) {
			setMapDisabledMessage(t("no_scans_have_been_made"));
		} else if(locations.length === 0) {
			setMapDisabledMessage(t("scans_have_been_made_but_no_locations_have_been_shared"));
		} else {
			// This ensabled the map
			setMapDisabledMessage("");
		}
	}, [scans, locations]);

	useEffect(() => {
		updateListOfScans();
		setSelectedScanInList(null);
	}, [scanToken]);

	// Auto-refresh when user returns to this browser tab (e.g. after simulate scan)
	const scanTokenRef = useRef(scanToken);
	scanTokenRef.current = scanToken;
	useEffect(() => {
		const handleVisibilityChange = () => {
			if (document.visibilityState === 'visible' && scanTokenRef.current) {
				updateListOfScans();
			}
		};
		document.addEventListener('visibilitychange', handleVisibilityChange);
		return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
	}, []);

	useEffect(() => {
		setSelectedScanInList(null);
	}, [scans]);

	const handleScanClickInMap = (scanId) => {
		const clickedScan = scans.find(scan => scan.id === scanId);
		if (clickedScan) {
			setSelectedScanInList(clickedScan);
		}
	};

	const handleScanClickInList = (scan) => {
		setSelectedScanInList(scan);
	};

	return (
		<>
			<div className="w-full mx-auto py-4">
				<div className="flex items-center justify-center gap-3 mb-4">
					<h2 className="text-center font-bold text-3xl">{t("page_scans")}</h2>
					{scanToken && (
						<button
							onClick={updateListOfScans}
							title="Uppdatera skanningslistan"
							className="p-1.5 text-gray-500 hover:text-cyan-700 hover:bg-gray-100 rounded-full transition"
						>
							<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
							</svg>
						</button>
					)}
				</div>
				<div className="flex flex-row gap-4">
					{/* Scans details */}
					<div className="flex-1">
						<div className="relative mt-2">
							<div className="max-h-[80vh] overflow-auto rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
								{scans.map((scan) => (
									<div
										key={scan.id}
										onClick={() => handleScanClickInList(scan)}
										className={`cursor-pointer select-none relative py-2 pl-3 pr-9 ${
											selectedScanInList?.id === scan.id ? 'bg-gray-100' : ''
										}`}
									>
										<span>{`Date and time: ${formatTimestamp(scan.emergency_timestamp)}`}</span>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
				{userType == 'ADMIN' && selectedScanInList && (
					<div className="mt-4">
						<button
							onClick={() => setShowDetails(!showDetails)}
							className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded font-semibold text-sm"
						>
							{showDetails ? 'Hide Details ▲' : 'Show Details ▼'}
						</button>
						{showDetails && (
						<div className="mt-2 p-4 border rounded bg-gray-50 space-y-4">
							<div>
								<h3 className="text-xl font-bold mb-2">Selected Scan Details</h3>
								<div className="text-sm space-y-1">
									<p><span className="font-medium">ID:</span> {selectedScanInList.id}</p>
									<p><span className="font-medium">Scan Token ID:</span> {selectedScanInList.scan_token_id}</p>
									<p><span className="font-medium">Phone:</span> {selectedScanInList.phone || 'N/A'}</p>
									<p><span className="font-medium">Message:</span> {selectedScanInList.message || 'N/A'}</p>
									<p><span className="font-medium">Location:</span> {locationX}, {locationY}</p>
									<p><span className="font-medium">Emergency Timestamp:</span> {formatTimestamp(selectedScanInList.emergency_timestamp)}</p>
									<p><span className="font-medium">Emergency SMS Tracking ID:</span> {selectedScanInList.emergency_sms_tracking_id || 'N/A'}</p>
									<p><span className="font-medium">Location Timestamp:</span> {formatTimestamp(selectedScanInList.location_timestamp)}</p>
									<p><span className="font-medium">Location SMS Tracking ID:</span> {selectedScanInList.location_sms_tracking_id || 'N/A'}</p>
								</div>
							</div>

							{/* Medical snapshot from scan time */}
							{selectedScanInList.medical_snapshot ? (
								<div className="border-2 border-cyan-200 rounded-lg p-4 bg-cyan-50">
									<h4 className="text-sm font-semibold text-cyan-900 mb-3 flex items-center">
										<svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
										</svg>
										Medicinsk data vid skanningstillfället
									</h4>
									{selectedScanInList.medical_snapshot.bearer_name && (
										<p className="text-sm font-medium text-cyan-800 mb-2">{selectedScanInList.medical_snapshot.bearer_name}</p>
									)}
									<div className="bg-white rounded-lg p-4 shadow-sm text-sm">
										<BearerMedicalDisplay
											t={t}
											isVerbal={selectedScanInList.medical_snapshot.is_verbal}
											diseases={selectedScanInList.medical_snapshot.diseases || []}
											birthYear={selectedScanInList.medical_snapshot.birth_year || -1}
											sex={selectedScanInList.medical_snapshot.sex}
											bloodType={selectedScanInList.medical_snapshot.blood_type}
										/>
									</div>
									{selectedScanInList.medical_snapshot.emergency_contacts && selectedScanInList.medical_snapshot.emergency_contacts.length > 0 && (
										<div className="mt-3 text-sm">
											<span className="font-medium text-cyan-900">Nödkontakter:</span>
											<div className="mt-1 space-y-1">
												{selectedScanInList.medical_snapshot.emergency_contacts.map((c, i) => (
													<div key={i} className="bg-white rounded px-3 py-1.5 shadow-sm text-sm">
														{c.phone} — {c.description || 'Ingen beskrivning'}{c.is_primary ? ' (Primär)' : ''}
													</div>
												))}
											</div>
										</div>
									)}
									<p className="text-xs text-gray-400 mt-3">Snapshot tagen: {formatTimestamp(selectedScanInList.medical_snapshot.captured_at)}</p>
								</div>
							) : (
								<div className="p-3 border border-gray-200 rounded bg-gray-100 text-sm text-gray-500 italic">
									Ingen medicinsk snapshot tillgänglig för denna skanning.
								</div>
							)}
						</div>
						)}
					</div>
				)}

				<div>
					<ScanLocationsMap
						scansList={locations}
						handleScanClickInMap={handleScanClickInMap}
						selectedMarkerIdFromParent={selectedScanInList?.id}
						disabledMessage={mapDisabledMessage} />
				</div>
			</div>
		</>);
};

export default Scans;
